import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';

/**
 * True when the user has view-only Lead / Ticket Holder details (TPA). Admins — anyone who can manage roles —
 * are never view-only, even if the permission gets ticked for them. Mirrors ViewOnlyDetailsActionFilter on the server.
 */
export function isViewOnlyUser(isGranted: (permissionName: string) => boolean): boolean {
    return isGranted('Pages.Tenant.TTWOperation.ViewOnlyDetails') && !isGranted('Pages.Administration.Roles');
}

/** Controls that stay usable in view-only mode: navigation, paging, filters, closing dialogs. */
const ALLOWED_CONTAINERS = [
    '.view-allowed',
    '.p-accordion-header',
    '.p-paginator',
    '.p-tabview-nav',
    '.modal-header',
    '.p-dialog-header',
].join(', ');

/** Buttons that only reveal data (SSN eye, "view details" eye). */
const VIEW_ICONS = '.pi-eye, .pi-eye-slash, .fa-eye, .fa-eye-slash';

const CLOSE_LABELS = ['cancel', 'close'];

const STYLE_ID = 'app-view-only-styles';

/** Clickable non-form elements that change data (status flows, PrimeNG pickers, rich editors). */
const STYLES = `
.app-view-only .flow-node-clickable,
.app-view-only .flow-panel-item,
.app-view-only .p-dropdown,
.app-view-only .p-multiselect,
.app-view-only .p-calendar,
.app-view-only .p-inputswitch,
.app-view-only .p-checkbox,
.app-view-only .p-radiobutton,
.app-view-only .p-fileupload,
.app-view-only .ql-editor,
.app-view-only [contenteditable="true"] { pointer-events: none !important; }
.app-view-only .view-allowed .p-dropdown,
.app-view-only .view-allowed .p-multiselect,
.app-view-only .view-allowed .p-calendar { pointer-events: auto !important; }
`;

/**
 * Makes everything inside the host read-only: disables buttons and form controls (except navigation,
 * paging, filters, "view" eye buttons and dialog close/cancel) and blocks clicks on status flows and
 * PrimeNG pickers. A MutationObserver keeps controls disabled as sections expand or Angular re-binds
 * [disabled]. The server rejects writes for view-only users regardless; this is the UI side.
 *
 * Usage: <div [appViewOnly]="isViewOnly"> … </div>. Mark extra read-only controls with class "view-allowed".
 */
@Directive({
    selector: '[appViewOnly]',
    standalone: true,
})
export class ViewOnlyDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input('appViewOnly') enabled = false;

    private observer?: MutationObserver;
    private scheduled = false;
    private viewReady = false;

    constructor(private _el: ElementRef<HTMLElement>, private _zone: NgZone) {}

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.sync();
    }

    ngOnChanges(): void {
        if (this.viewReady) {
            this.sync();
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    private sync(): void {
        const host = this._el.nativeElement;
        host.classList.toggle('app-view-only', this.enabled);

        if (!this.enabled) {
            this.observer?.disconnect();
            this.observer = undefined;
            return;
        }

        ensureStyles();
        this.apply();
        if (!this.observer) {
            this._zone.runOutsideAngular(() => {
                this.observer = new MutationObserver(() => this.schedule());
                this.observer.observe(host, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['disabled'],
                });
            });
        }
    }

    private schedule(): void {
        if (this.scheduled) return;
        this.scheduled = true;
        requestAnimationFrame(() => {
            this.scheduled = false;
            this.apply();
        });
    }

    private apply(): void {
        const controls = this._el.nativeElement.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            'button, input, textarea, select'
        );
        controls.forEach((control) => {
            if (!control.disabled && !isAllowed(control)) {
                control.disabled = true;
            }
        });
    }
}

function isAllowed(control: Element): boolean {
    if (control.closest(ALLOWED_CONTAINERS) || control.classList.contains('btn-close')) {
        return true;
    }
    if (control.tagName === 'BUTTON') {
        if (control.querySelector(VIEW_ICONS)) return true;
        const label = (control.textContent || '').trim().toLowerCase();
        if (CLOSE_LABELS.includes(label)) return true;
    }
    return false;
}

function ensureStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
}
